import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Scale, Phone, Mail, Globe, MapPin } from "lucide-react";
import { api } from "../lib/api";

export const PengumumanPopup = () => {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let timer;
    api
      .get("/pengumuman")
      .then((r) => {
        if (r.data?.aktif) {
          setData(r.data);
          timer = setTimeout(() => setOpen(true), 900);
        }
      })
      .catch(() => {});
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    if (open) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  if (!data) return null;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="pengumuman-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => setOpen(false)}
          className="fixed inset-0 z-[95] flex items-center justify-center bg-black/75 p-4 backdrop-blur-sm"
        >
          <motion.div
            data-testid="pengumuman-popup"
            initial={{ opacity: 0, scale: 0.92, y: 24 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.94, y: 16 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="Pengumuman"
            className="relative max-h-[90vh] w-full max-w-md overflow-y-auto border-4 border-[#C5A059] bg-[#FAF6EE] shadow-2xl outline outline-1 outline-offset-4 outline-[#C5A059]/60"
          >
            <button
              onClick={() => setOpen(false)}
              aria-label="Tutup pengumuman"
              data-testid="pengumuman-close-x"
              className="absolute right-3 top-3 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-[#1B4332] text-white transition-all duration-300 hover:rotate-90 hover:bg-[#C5A059] hover:text-[#1B4332]"
            >
              <X size={18} />
            </button>

            <div className="px-7 pb-7 pt-8 text-center">
              <div className="flex items-start justify-between">
                <img
                  src="/logo-full.png"
                  alt="Logo YLBH Sumatera Timur"
                  className="h-14 w-auto"
                />
                <img
                  src="/peradi-logo.png"
                  alt="Logo PERADI — Perhimpunan Advokat Indonesia"
                  className="mr-9 h-12 w-auto"
                />
              </div>

              <p className="mt-6 text-[11px] font-bold uppercase tracking-[0.4em] text-[#C5A059]">
                Pengumuman
              </p>
              <h2 className="mt-2 text-[15px] font-bold uppercase tracking-[0.25em] text-[#1a1a1a]">
                {data.judul_atas}
              </h2>
              <h3 className="mt-3 text-xl font-extrabold leading-snug text-[#1a1a1a] sm:text-2xl">
                {data.judul}
              </h3>

              <div className="my-6 flex items-center justify-center gap-3" aria-hidden="true">
                <span className="h-px w-16 bg-[#C5A059]" />
                <Scale size={20} className="text-[#C5A059]" />
                <span className="h-px w-16 bg-[#C5A059]" />
              </div>

              <div className="border-2 border-[#C5A059] bg-[#1B4332] px-6 py-6 text-left">
                <p className="text-center text-lg font-bold text-white">
                  {data.label_jadwal}
                </p>
                <div className="mx-auto my-3 flex w-24 items-center gap-2" aria-hidden="true">
                  <span className="h-px flex-1 bg-[#C5A059]" />
                  <span className="h-1.5 w-1.5 rotate-45 bg-[#C5A059]" />
                  <span className="h-px flex-1 bg-[#C5A059]" />
                </div>
                <p
                  data-testid="pengumuman-jadwal"
                  className="text-center text-xl font-extrabold tracking-wide text-white"
                >
                  {data.jadwal}
                </p>
                <p className="mt-3 flex items-start justify-center gap-2 text-center text-sm font-medium text-white/85">
                  <MapPin size={14} className="mt-0.5 shrink-0 text-[#C5A059]" />
                  {data.sekretariat}
                </p>
                <p className="mt-4 flex items-start justify-center gap-2 text-center text-sm font-semibold text-white">
                  <Phone size={14} className="mt-0.5 shrink-0 text-[#C5A059]" />
                  {data.kontak}
                </p>
              </div>

              <div className="mt-5 flex flex-col items-center justify-center gap-2 sm:flex-row sm:gap-3">
                <a
                  href={`mailto:${data.email}`}
                  className="flex items-center gap-2 border border-[#1B4332]/30 px-3 py-1.5 text-xs font-semibold text-[#1B4332] transition-colors hover:border-[#C5A059]"
                >
                  <Mail size={13} className="text-[#C5A059]" /> {data.email}
                </a>
                <span className="flex items-center gap-2 border border-[#1B4332]/30 px-3 py-1.5 text-xs font-semibold text-[#1B4332]">
                  <Globe size={13} className="text-[#C5A059]" /> {data.website}
                </span>
              </div>

              <button
                onClick={() => setOpen(false)}
                data-testid="pengumuman-close-button"
                className="mt-6 w-full bg-[#1B4332] py-3.5 text-[13px] font-bold uppercase tracking-[0.25em] text-white transition-all duration-300 hover:bg-[#143527] active:scale-[0.98]"
              >
                Tutup
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
